import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Tabs,
  Select,
  Form,
  Input,
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Space,
  notification,
} from "antd";
import {
  BiMessageSquareX,
  BiMessageSquareCheck,
  BiSearch,
  BiRefresh,
  BiUserPlus,
} from "react-icons/bi";
import {
  FaEye,
  FaPencilAlt,
  FaUserTie,
  FaChalkboardTeacher,
  FaUserCheck,
  FaUserSlash,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getUsers,
  blockAuser,
  resetState,
} from "../features/cutomers/customerSlice";
import { getStates } from "../features/statecity/statecitySlice";
import { Link } from "react-router-dom";
import CustomModal from "../components/CustomModal";
import CustomerPopupModal from "../components/CustomerPopupModal";
import { CSVLink } from "react-csv";
import { ExportOutlined } from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;

// Helper to normalize role check
const isRoleVC = (role) => {
  const r = (role || "").trim().toLowerCase();
  return r === "vc" || r === "vocational co-ordinator" || r === "vocational coordinator";
};

const Customers = () => {
  const [form] = Form.useForm();
  
  // Modals state
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  
  // Filter state
  const [selectedState, setSelectedState] = useState("");
  const [selectedVC, setSelectedVC] = useState("");
  const [selectedVT, setSelectedVT] = useState("");
  const [selectedTab, setSelectedTab] = useState("1"); // 1: VC, 2: VT
  const [searchQuery, setSearchQuery] = useState("");
  
  const [vcDropdownList, setVcDropdownList] = useState([]);
  const [vtDropdownList, setVtDropdownList] = useState([]);

  const dispatch = useDispatch();
  const statelistState = useSelector((state) => state.statecity.statelist || []);
  const customerstate = useSelector((state) => state.customer.customers || []);
  const isLoading = useSelector((state) => state.customer.isLoading);

  useEffect(() => {
    dispatch(resetState());
    dispatch(getUsers());
    dispatch(getStates());
  }, [dispatch]);

  // Calculate statistics counts
  const stats = useMemo(() => {
    const validUsers = customerstate.filter(
      (c) =>
        c.role !== "admin" &&
        c.role !== "subadmin" &&
        c.role !== "hr" &&
        c.role !== "accountant"
    );
    const totalVC = validUsers.filter((c) => isRoleVC(c.role)).length;
    const totalVT = validUsers.filter((c) => !isRoleVC(c.role)).length;
    const totalActive = validUsers.filter((c) => !c.isBlocked).length;
    const totalBlocked = validUsers.filter((c) => c.isBlocked).length;
    return { totalVC, totalVT, totalActive, totalBlocked, totalAll: validUsers.length };
  }, [customerstate]);

  // Handle State Dropdown change for cascading filter
  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedVC("");
    setSelectedVT("");
    form.setFieldsValue({ vc_name: undefined, vt_name: undefined });

    if (value) {
      const vcsInState = customerstate.filter(
        (c) => (c.state === value || c.state_name === value) && isRoleVC(c.role)
      );
      setVcDropdownList(vcsInState);
      setVtDropdownList([]);
    } else {
      setVcDropdownList([]);
      setVtDropdownList([]);
    }
  };

  // Handle VC Dropdown change
  const handleVcChange = (value) => {
    setSelectedVC(value);
    setSelectedVT("");
    form.setFieldsValue({ vt_name: undefined });

    if (value) {
      const vtsUnderVc = customerstate.filter(
        (c) => c.vcuser_id === value && !isRoleVC(c.role)
      );
      setVtDropdownList(vtsUnderVc);
    } else {
      setVtDropdownList([]);
    }
  };

  const handleVTChange = (value) => {
    setSelectedVT(value);
  };

  // Clear all filters
  const handleClearSearch = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedVC("");
    setSelectedVT("");
    setSearchQuery("");
    setVcDropdownList([]);
    setVtDropdownList([]);
  };

  // Primary filtering pipeline
  const filteredUsers = useMemo(() => {
    return customerstate
      .filter((customer) => {
        // Exclude system management roles from general directory
        if (
          customer.role === "admin" ||
          customer.role === "subadmin" ||
          customer.role === "hr" ||
          customer.role === "accountant"
        ) {
          return false;
        }

        const isVC = isRoleVC(customer.role);

        // 1. Always enforce Active Tab filter (VC vs VT)
        if (selectedTab === "1" && !isVC) return false;
        if (selectedTab === "2" && isVC) return false;

        // 2. If search query is present, search within current active tab
        if (searchQuery && searchQuery.trim() !== "") {
          const q = searchQuery.trim().toLowerCase();
          const name = (customer.name || "").toLowerCase();
          const email = (customer.email || "").toLowerCase();
          const uniqueId = String(customer.unique_id || "").toLowerCase();
          const phone = String(customer.phone_no || customer.mobile || "").toLowerCase();
          const stateName = (customer.state_name || customer.state || "").toLowerCase();
          const district = (customer.district || "").toLowerCase();
          const qualification = (customer.qualification || "").toLowerCase();
          const roleStr = (customer.role || "").toLowerCase();

          const matches =
            name.includes(q) ||
            email.includes(q) ||
            uniqueId.includes(q) ||
            phone.includes(q) ||
            stateName.includes(q) ||
            district.includes(q) ||
            qualification.includes(q) ||
            roleStr.includes(q);

          if (!matches) return false;
        }
        // 3. Dropdown Hierarchy Filters
        if (selectedState) {
          const matchState =
            customer.state === selectedState ||
            customer.state_name === selectedState;
          if (!matchState) return false;
        }

        if (selectedVC) {
          if (isVC && customer._id !== selectedVC) return false;
          if (!isVC && customer.vcuser_id !== selectedVC) return false;
        }

        if (selectedVT) {
          if (customer._id !== selectedVT) return false;
        }

        return true;
      });
  }, [customerstate, selectedTab, selectedState, selectedVC, selectedVT, searchQuery]);

  // Form submission for dropdown filters
  const onFinishFilter = () => {
    if (filteredUsers.length === 0) {
      notification.info({
        message: "No Results",
        description: "No users matched the selected filter criteria.",
        placement: "topRight",
      });
    }
  };

  // Modals trigger functions
  const showBlockModalHandler = (user, blocked) => {
    setSelectedUser(user);
    setIsBlocked(blocked);
    setOpenBlockModal(true);
  };

  const openViewModalHandler = (user) => {
    setSelectedUser(user);
    setOpenViewModal(true);
  };

  const executeBlockCustomer = (id, blocked) => {
    dispatch(blockAuser({ customerId: id, isBlocked: blocked }));
    setOpenBlockModal(false);
    toast.info(blocked ? "User Unblocked successfully" : "User Blocked successfully");
    setTimeout(() => {
      dispatch(getUsers());
    }, 300);
  };

  // Table Columns Definition
  const columns = [
    {
      title: "S.No",
      dataIndex: "key",
      width: 65,
      align: "center",
      render: (text) => <span style={{ fontWeight: 600, color: "#64748b" }}>{text}</span>,
    },
    {
      title: "Unique ID",
      dataIndex: "unique_id",
      width: 130,
      render: (text) => (
        <Tag color="blue" style={{ fontWeight: 600, fontSize: "13px", padding: "2px 8px", borderRadius: "4px" }}>
          {text || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Name & Role",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (text, record) => {
        const isVC = isRoleVC(record.role);
        return (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}>{text || "N/A"}</div>
            <div style={{ marginTop: 2 }}>
              <Tag color={isVC ? "geekblue" : "purple"} style={{ fontSize: "11px", borderRadius: "3px" }}>
                {isVC ? "Vocational Co-ordinator" : "Vocational Trainer"}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "Email Address",
      dataIndex: "email",
      render: (text) => (
        <span style={{ color: "#3b82f6", fontWeight: 500 }}>
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Mobile Number",
      dataIndex: "mobile",
      render: (text) => (
        <span style={{ color: "#334155", fontWeight: 500 }}>
          {text ? `+91 ${text}` : "N/A"}
        </span>
      ),
    },
    {
      title: "Location",
      dataIndex: "state",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, color: "#1e293b" }}>{record.state || record.state_name || "N/A"}</div>
          {record.district && (
            <div style={{ fontSize: "12px", color: "#64748b" }}>{record.district}</div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isBlocked",
      align: "center",
      width: 100,
      render: (blocked) => (
        <Tag color={blocked ? "error" : "success"} style={{ borderRadius: "12px", padding: "1px 10px", fontWeight: 600 }}>
          {blocked ? "Blocked" : "Active"}
        </Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      align: "center",
      width: 130,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Profile Details">
            <Button
              type="text"
              shape="circle"
              onClick={() => openViewModalHandler(record)}
              style={{ color: "#2563eb", background: "#eff6ff" }}
              icon={<FaEye />}
            />
          </Tooltip>

          <Tooltip title={record.isBlocked ? "Click to Unblock User" : "Click to Block User"}>
            <Button
              type="text"
              shape="circle"
              onClick={() => showBlockModalHandler(record, record.isBlocked)}
              style={{
                color: record.isBlocked ? "#dc2626" : "#16a34a",
                background: record.isBlocked ? "#fef2f2" : "#f0fdf4",
              }}
              icon={record.isBlocked ? <BiMessageSquareX /> : <BiMessageSquareCheck />}
            />
          </Tooltip>

          <Tooltip title="Edit User">
            <Link to={`/admin/vtvc/${record.id}`}>
              <Button
                type="text"
                shape="circle"
                style={{ color: "#d97706", background: "#fffbeb" }}
                icon={<FaPencilAlt />}
              />
            </Link>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Table Data Source Mapping
  const transformedData = useMemo(() => {
    return filteredUsers.map((customer, index) => ({
      key: index + 1,
      id: customer._id,
      _id: customer._id,
      unique_id: customer.unique_id,
      name: customer.name,
      email: customer.email,
      mobile: customer.phone_no,
      state: customer.state_name || customer.state,
      district: customer.district,
      role: customer.role,
      isBlocked: customer.isBlocked,
    }));
  }, [filteredUsers]);

  const csvHeaders = [
    { label: "S. No", key: "key" },
    { label: "Unique ID", key: "unique_id" },
    { label: "Name", key: "name" },
    { label: "Role", key: "role" },
    { label: "Email", key: "email" },
    { label: "Mobile", key: "mobile" },
    { label: "State", key: "state" },
    { label: "District", key: "district" },
    { label: "Status", key: "isBlocked" },
  ];

  return (
    <div style={{ padding: "16px 24px", minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      {/* Header & Create Button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h2 style={{ margin: 0, color: "#0f172a", fontSize: "24px", fontWeight: 700 }}>
            Registered VT / VC Directory
          </h2>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "14px" }}>
            Manage, edit, or block Vocational Co-ordinators and Vocational Trainers
          </p>
        </div>

        <Space>
          <Link to="/admin/vtvc">
            <Button
              type="primary"
              size="large"
              icon={<BiUserPlus style={{ fontSize: "18px" }} />}
              style={{
                backgroundColor: "#16a34a",
                borderColor: "#16a34a",
                borderRadius: "8px",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(22, 163, 74, 0.2)",
              }}
            >
              Register VT or VC
            </Button>
          </Link>
          <Button
            size="large"
            icon={<BiRefresh style={{ fontSize: "18px" }} />}
            onClick={() => dispatch(getUsers())}
            style={{ borderRadius: "8px" }}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Stats Counter Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        <Col xs={12} sm={6} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderLeft: "4px solid #2563eb",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>Total VCs</div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#0f172a", marginTop: "4px" }}>
                  {stats.totalVC}
                </div>
              </div>
              <div style={{ background: "#eff6ff", padding: "10px", borderRadius: "10px", color: "#2563eb" }}>
                <FaUserTie style={{ fontSize: "20px" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderLeft: "4px solid #8b5cf6",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>Total VTs</div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#0f172a", marginTop: "4px" }}>
                  {stats.totalVT}
                </div>
              </div>
              <div style={{ background: "#f5f3ff", padding: "10px", borderRadius: "10px", color: "#8b5cf6" }}>
                <FaChalkboardTeacher style={{ fontSize: "20px" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderLeft: "4px solid #16a34a",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>Active Users</div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#16a34a", marginTop: "4px" }}>
                  {stats.totalActive}
                </div>
              </div>
              <div style={{ background: "#f0fdf4", padding: "10px", borderRadius: "10px", color: "#16a34a" }}>
                <FaUserCheck style={{ fontSize: "20px" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderLeft: "4px solid #ef4444",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>Blocked Users</div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444", marginTop: "4px" }}>
                  {stats.totalBlocked}
                </div>
              </div>
              <div style={{ background: "#fef2f2", padding: "10px", borderRadius: "10px", color: "#ef4444" }}>
                <FaUserSlash style={{ fontSize: "20px" }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Advanced Filter Card */}
      <Card
        bordered={false}
        style={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          marginBottom: "20px",
        }}
        bodyStyle={{ padding: "18px 20px" }}
      >
        <div style={{ fontWeight: 600, fontSize: "15px", color: "#334155", marginBottom: "14px" }}>
          Filter By Hierarchy & Location
        </div>
        <Form form={form} layout="inline" onFinish={onFinishFilter} style={{ gap: "12px" }}>
          <Form.Item name="state_name" style={{ margin: 0, minWidth: "180px" }}>
            <Select
              placeholder="Select State"
              onChange={handleStateChange}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            >
              {statelistState.map((i) => (
                <Option key={i.code} value={i.code}>
                  {i.state_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="vc_name" style={{ margin: 0, minWidth: "180px" }}>
            <Select
              placeholder="Select VC"
              onChange={handleVcChange}
              allowClear
              disabled={!selectedState}
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            >
              {vcDropdownList.map((item) => (
                <Option key={item._id} value={item._id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="vt_name" style={{ margin: 0, minWidth: "180px" }}>
            <Select
              placeholder="Select VT"
              onChange={handleVTChange}
              allowClear
              disabled={!selectedVC}
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            >
              {vtDropdownList.map((item) => (
                <Option key={item._id} value={item._id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ margin: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<BiSearch style={{ fontSize: "16px" }} />}
              style={{ borderRadius: "6px", backgroundColor: "#2563eb", fontWeight: 500 }}
            >
              Apply Filter
            </Button>
          </Form.Item>

          <Form.Item style={{ margin: 0 }}>
            <Button
              onClick={handleClearSearch}
              style={{ borderRadius: "6px", fontWeight: 500 }}
            >
              Reset Filters
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Main Table Card with Tabs and Realtime Search */}
      <Card
        bordered={false}
        style={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
        bodyStyle={{ padding: "20px" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "14px", marginBottom: "16px" }}>
          <Tabs
            activeKey={selectedTab}
            onChange={(key) => setSelectedTab(key)}
            style={{ margin: 0 }}
            items={[
              {
                key: "1",
                label: "Vocational Co-ordinators",
              },
              {
                key: "2",
                label: "Vocational Trainers",
              },
            ]}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Search
              placeholder="Search by Name, Email, Phone, Unique ID, State..."
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 340 }}
              size="middle"
            />

            <CSVLink
              data={transformedData}
              headers={csvHeaders}
              filename={`registered_users_${new Date().toISOString().slice(0, 10)}.csv`}
            >
              <Button icon={<ExportOutlined />} style={{ borderRadius: "6px", fontWeight: 500 }}>
                Export CSV ({transformedData.length})
              </Button>
            </CSVLink>
          </div>
        </div>

        <Table
          loading={isLoading}
          columns={columns}
          dataSource={transformedData}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
          }}
          rowClassName={(_, index) => (index % 2 === 0 ? "table-row-light" : "table-row-white")}
          scroll={{ x: 900 }}
          style={{ borderRadius: "8px" }}
        />
      </Card>

      {/* User Information Details Modal */}
      <CustomerPopupModal
        open={openViewModal}
        hideModal={() => setOpenViewModal(false)}
        user_info={customerstate}
        user_id={selectedUser?._id || selectedUser?.id}
      />

      {/* Block / Unblock Confirmation Modal */}
      <CustomModal
        hideModal={() => setOpenBlockModal(false)}
        open={openBlockModal}
        performAction={() => executeBlockCustomer(selectedUser?._id || selectedUser?.id, isBlocked)}
        title={
          <div style={{ fontSize: "16px", fontWeight: 600 }}>
            {isBlocked ? "Unblock this User?" : "Block this User?"}
          </div>
        }
      />
    </div>
  );
};

export default Customers;
